import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTenantCapabilities } from '@/pages/access-admin/tenant/feature';

import type { NotificationItem } from './api';
import { inAppPath } from './format';
import { canOpenTarget } from './targets';
import { markRead } from './useNotifications';

/**
 * Where an item leads for the active role: its `action.href` when that is an in-app route
 * (inAppPath) the role may open (targets.ts); otherwise null.
 */
export function useNotificationTarget() {
  const caps = useTenantCapabilities();
  return useCallback(
    (item: NotificationItem): string | null => {
      const path = inAppPath(item.action?.href);
      return path && canOpenTarget(path, caps) ? path : null;
    },
    [caps],
  );
}

/**
 * Opening an item: it is marked read (`POST me/notifications/{id}/read`, optimistic), then the
 * app goes to its target (useNotificationTarget) inside the app, without a reload. `onOpened`
 * runs before that navigation (the panel closes); an item without a target is only marked read.
 */
export function useOpenNotification(onOpened?: () => void) {
  const navigate = useNavigate();
  const targetOf = useNotificationTarget();
  return useCallback(
    (item: NotificationItem) => {
      markRead(item);
      const path = targetOf(item);
      if (!path) return;
      onOpened?.();
      navigate(path);
    },
    [navigate, onOpened, targetOf],
  );
}
